/**
 * OffertUploadDialog — manual offer upload (P2 Placement Slice 10, D3). A PDF file input (client-side
 * type + 20 MB size check), an optional «zuordenbar» submission select («- spaeter zuordnen -» first),
 * and the mapped server error codes.
 */

import { useState } from 'react';
import { Dialog } from '../administration/components';
import { Button } from '../../components/ui/Button';
import {
  OFFER_PLACEMENT_STATUS_HINWEIS,
  OFFER_UPLOAD_MAX_BYTES,
  OFFER_UPLOAD_NUR_PDF_HINWEIS,
} from '@brokly/shared';
import type { OfferSubmissionGruppe } from '@brokly/shared';

function mapError(code: string | null): string | null {
  if (!code) return null;
  switch (code) {
    case 'offer_duplicate':
      return 'Diese Datei ist bereits als Offerte erfasst.';
    case 'placement_status_invalid':
      return OFFER_PLACEMENT_STATUS_HINWEIS;
    case 'submission_not_sendable':
      return 'Diesem Versicherer wurde noch keine Offertanfrage gesendet.';
    case 'file_type_invalid':
      return OFFER_UPLOAD_NUR_PDF_HINWEIS;
    default:
      return code;
  }
}

export function OffertUploadDialog({
  open,
  onClose,
  gruppen,
  onUpload,
  uploading,
  error,
}: {
  open: boolean;
  onClose: () => void;
  gruppen: OfferSubmissionGruppe[];
  onUpload: (file: File, submissionId?: string) => void;
  uploading: boolean;
  error: string | null;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [submissionId, setSubmissionId] = useState<string>('');
  const [clientError, setClientError] = useState<string | null>(null);

  const zuordenbar = gruppen.filter((g) => g.submission_id !== null && g.zuordenbar);

  const pick = (f: File | null) => {
    setClientError(null);
    if (!f) {
      setFile(null);
      return;
    }
    if (f.type !== 'application/pdf' || !f.name.toLowerCase().endsWith('.pdf') || f.size > OFFER_UPLOAD_MAX_BYTES) {
      setClientError(OFFER_UPLOAD_NUR_PDF_HINWEIS);
      setFile(null);
      return;
    }
    setFile(f);
  };

  const shownError = clientError ?? mapError(error);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Offerte hochladen"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button
            disabled={!file || uploading}
            onClick={() => file && onUpload(file, submissionId || undefined)}
            data-testid="offert-upload-submit"
          >
            {uploading ? 'Wird hochgeladen …' : 'Hochladen'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3" data-testid="offert-upload-dialog">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">PDF-Datei</span>
          <input
            type="file"
            accept="application/pdf"
            onChange={(e) => pick(e.target.files?.[0] ?? null)}
            data-testid="offert-upload-file"
          />
          <span className="text-xs text-muted-foreground">{OFFER_UPLOAD_NUR_PDF_HINWEIS}</span>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">Zuordnung (optional)</span>
          <select
            className="rounded-md border border-border bg-background p-2 text-sm"
            value={submissionId}
            onChange={(e) => setSubmissionId(e.target.value)}
            data-testid="offert-upload-submission"
          >
            <option value="">- spaeter zuordnen -</option>
            {zuordenbar.map((g) => (
              <option key={g.submission_id!} value={g.submission_id!}>
                {(g.name ?? g.carrier_uid) + ' (' + g.submission_number + ')'}
              </option>
            ))}
          </select>
        </label>
        {shownError && (
          <p className="text-sm text-status-danger" role="alert" data-testid="offert-upload-error">
            {shownError}
          </p>
        )}
      </div>
    </Dialog>
  );
}
